import {
  type ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { Link } from "react-router-dom-v5-compat";
import { IconNameEnum, useModuleBEM } from "@b2bportal/core-themes";
import { useI18nContext } from "@hopper-b2b/i18n";
import { Icon } from "@tenant/ui";
import clsx from "clsx";
import { DealTileSkeleton } from "../DealTile";
import { TravelDealTile } from "../TravelDealTile";
import type { TravelDeal } from "../useTravelDeals";
import styles from "./TravelDealsCarousel.module.scss";
import type { OfferScreen } from "@/tracking/offers";
import { TenantIconNameEnum } from "@/types/tenantIcons";

// Skeleton tiles shown while loading.
const SKELETON_COUNT = 4;

interface CarouselBaseProps {
  title: string;
  /** "View all" link; omitted → no link under the title. */
  viewAll?: { label: string; to: string };
  className?: string;
}

export type TravelDealsCarouselProps = CarouselBaseProps &
  (
    | {
        deals: TravelDeal[];
        /** Surface the carousel renders on; segments the offer events. */
        screen: OfferScreen;
        isLoading?: boolean;
        children?: never;
      }
    | {
        /** Custom row content; replaces the default deal tiles. */
        children: ReactNode;
        deals?: never;
        screen?: never;
        isLoading?: never;
      }
  );

/**
 * Horizontally scrolling shelf of travel deal tiles with a heading and
 * prev/next buttons (Figma 40548-40221). Pure presentation: feed it
 * `useTravelDeals` output and place it in any page container, or pass
 * `children` to render custom cards in the scrollable row.
 */
export const TravelDealsCarousel = ({
  deals,
  isLoading = false,
  screen,
  title,
  viewAll,
  className,
  children,
}: TravelDealsCarouselProps) => {
  const { t } = useI18nContext();
  const [block, cn] = useModuleBEM(styles, "TravelDealsCarousel");

  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScroll, setCanScroll] = useState({ prev: false, next: false });

  // Prev/next only matter when the row overflows.
  const updateScrollState = useCallback(() => {
    const el = scrollRef.current;
    if (el == null) return;
    setCanScroll({
      prev: el.scrollLeft > 0,
      next: el.scrollLeft < el.scrollWidth - el.clientWidth - 1,
    });
  }, []);

  useEffect(() => {
    updateScrollState();
    window.addEventListener("resize", updateScrollState);
    return () => window.removeEventListener("resize", updateScrollState);
  }, [updateScrollState, deals?.length, isLoading]);

  const scrollByPage = (direction: 1 | -1) => {
    const el = scrollRef.current;
    if (el == null) return;
    el.scrollBy({ left: direction * el.clientWidth, behavior: "smooth" });
  };

  const showNav = canScroll.prev || canScroll.next;

  return (
    <div className={clsx(block, className)}>
      <div className={cn("header")}>
        <div className={cn("heading")}>
          <h2 className={cn("title")}>{title}</h2>
          {viewAll != null && (
            <Link className={cn("view-all")} to={viewAll.to}>
              {viewAll.label}
              <Icon iconName={TenantIconNameEnum.arrowRightAlt} aria-hidden />
            </Link>
          )}
        </div>
        {showNav && (
          <div className={cn("nav")}>
            <button
              type="button"
              className={cn("nav-button")}
              onClick={() => scrollByPage(-1)}
              disabled={!canScroll.prev}
              aria-label={t("travelDealsCarousel.previous")}
            >
              <Icon iconName={IconNameEnum.leftNavChevron} useCurrentColor />
            </button>
            <button
              type="button"
              className={cn("nav-button")}
              onClick={() => scrollByPage(1)}
              disabled={!canScroll.next}
              aria-label={t("travelDealsCarousel.next")}
            >
              <Icon iconName={IconNameEnum.rightNavChevron} useCurrentColor />
            </button>
          </div>
        )}
      </div>
      <div ref={scrollRef} className={cn("row")} onScroll={updateScrollState}>
        {children ??
          (isLoading
            ? Array.from({ length: SKELETON_COUNT }).map((_, index) => (
                <DealTileSkeleton key={index} />
              ))
            : deals?.map((deal, index) => (
                <TravelDealTile
                  key={deal.key}
                  deal={deal}
                  tileIndex={index}
                  screen={screen}
                />
              )))}
      </div>
    </div>
  );
};
